import { diffWords } from "diff";
import { getDocument } from "pdfjs-dist/legacy/webpack.mjs";

const STORAGE_KEY = "tax_savy_pdf_store"; // stores an array of { id, label, pdfType, ts, base64 }
const MAX_STORED_PDFS_PER_TYPE = 3;
const PDF_RENDER_SCALE = 1.5;
const PIXEL_DIFF_THRESHOLD = 48;
const DIFF_TILE_SIZE = 10;
const MIN_CHANGED_PIXELS_PER_TILE = 2;
const MIN_CHANGED_PIXELS_PER_BOX = 4;
const HIGHLIGHT_PADDING = 3;

function arrayBufferToBase64(buffer) {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000; // process in chunks to avoid call stack limits
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, chunk);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64) {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

function decodePdfLiteralString(str) {
  // Remove surrounding parentheses if present
  if (str.startsWith("(") && str.endsWith(")")) {
    str = str.slice(1, -1);
  }
  // Unescape common sequences used in PDF literal strings
  return (
    str
      .replace(/\\\(/g, "(")
      .replace(/\\\)/g, ")")
      .replace(/\\n/g, "\n")
      .replace(/\\r/g, "\r")
      .replace(/\\t/g, "\t")
      .replace(/\\b/g, "\b")
      .replace(/\\f/g, "\f")
      // Octal escapes like \053
      .replace(/\\([0-7]{1,3})/g, (_, oct) =>
        String.fromCharCode(parseInt(oct, 8))
      )
      .replace(/\\\\/g, "\\")
  );
}

function hexToString(hex) {
  const clean = hex.replace(/[^0-9a-fA-F]/g, "");
  const bytes = [];
  for (let i = 0; i < clean.length; i += 2) {
    const byte = clean.slice(i, i + 2);
    if (byte.length === 2) bytes.push(parseInt(byte, 16));
  }
  return String.fromCharCode.apply(null, bytes);
}

function skipWhitespace(content, start) {
  let index = start;
  while (index < content.length && /\s/.test(content[index])) index += 1;
  return index;
}

function readPdfLiteralString(content, start) {
  let depth = 0;
  let escaped = false;

  for (let index = start; index < content.length; index += 1) {
    const char = content[index];
    if (escaped) {
      escaped = false;
      continue;
    }
    if (char === "\\") {
      escaped = true;
      continue;
    }
    if (char === "(") depth += 1;
    if (char === ")" && --depth === 0) {
      return { value: content.slice(start, index + 1), end: index + 1 };
    }
  }

  return null;
}

function readPdfHexString(content, start) {
  const end = content.indexOf(">", start + 1);
  if (end === -1) return null;
  return { value: content.slice(start, end + 1), end: end + 1 };
}

function readPdfArray(content, start) {
  let depth = 0;

  for (let index = start; index < content.length; index += 1) {
    const char = content[index];
    if (char === "(") {
      const literal = readPdfLiteralString(content, index);
      if (!literal) return null;
      index = literal.end - 1;
      continue;
    }
    if (char === "[") depth += 1;
    if (char === "]" && --depth === 0) {
      return { value: content.slice(start + 1, index), end: index + 1 };
    }
  }

  return null;
}

function hasFollowingOperator(content, start, operator) {
  const operatorStart = skipWhitespace(content, start);
  return (
    content.slice(operatorStart, operatorStart + operator.length) === operator
  );
}

function extractTextFromPdfArray(content) {
  const texts = [];

  for (let index = 0; index < content.length; index += 1) {
    const char = content[index];
    if (char === "(") {
      const literal = readPdfLiteralString(content, index);
      if (!literal) break;
      texts.push(decodePdfLiteralString(literal.value));
      index = literal.end - 1;
    } else if (char === "<" && content[index + 1] !== "<") {
      const hex = readPdfHexString(content, index);
      if (!hex) break;
      texts.push(hexToString(hex.value.slice(1, -1)));
      index = hex.end - 1;
    }
  }

  return texts;
}

function extractTextFromArrayBuffer(buffer) {
  // Lightweight text extraction by scanning content streams for Tj/TJ operators.
  // This is a heuristic that works well for many jsPDF-generated PDFs
  const content = new TextDecoder("latin1").decode(new Uint8Array(buffer));
  const texts = [];

  for (let index = 0; index < content.length; index += 1) {
    const char = content[index];
    if (char === "(") {
      const literal = readPdfLiteralString(content, index);
      if (!literal) break;
      if (hasFollowingOperator(content, literal.end, "Tj")) {
        texts.push(decodePdfLiteralString(literal.value));
      }
      index = literal.end - 1;
    } else if (char === "<" && content[index + 1] !== "<") {
      const hex = readPdfHexString(content, index);
      if (!hex) break;
      if (hasFollowingOperator(content, hex.end, "Tj")) {
        texts.push(hexToString(hex.value.slice(1, -1)));
      }
      index = hex.end - 1;
    } else if (char === "[") {
      const array = readPdfArray(content, index);
      if (!array) break;
      if (hasFollowingOperator(content, array.end, "TJ")) {
        texts.push(...extractTextFromPdfArray(array.value));
      }
      index = array.end - 1;
    }
  }

  // Join and normalize whitespace for human readability
  return texts
    .join(" ")
    .replace(/[\r\t]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function countWords(value) {
  return value.split(/\s+/).filter(Boolean).length;
}

function generateDiffHtml(oldText, newText, title = "PDF Diff", options = {}) {
  const {
    storedLabel = "Stored PDF",
    currentLabel = "Current PDF",
    previousPdfUrl,
    currentPdfUrl,
    faviconUrl,
  } = options;
  const diff = diffWords(oldText, newText);
  const hasOriginalPdfs = Boolean(previousPdfUrl && currentPdfUrl);

  const styles = `
    * { box-sizing: border-box; }
    html, body { height: 100%; margin: 0; }
    body { display: flex; flex-direction: column; color: #334155; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Liberation Sans', sans-serif; }
    header { display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: #0f172a; color: #fff; }
    header h1 { margin: 0; font-size: 16px; font-weight: 600; }
    header .meta { opacity: 0.85; font-size: 12px; margin-top: 4px; }
    .toolbar { display: flex; gap: 8px; margin-left: auto; }
    button { padding: 6px 10px; border: 1px solid #475569; border-radius: 6px; background: #1e293b; color: #fff; cursor: pointer; font-size: 12px; }
    button:hover, button.active { background: #475569; }
    .summary { display: flex; gap: 18px; padding: 10px 16px; border-bottom: 1px solid #e2e8f0; background: #f8fafc; }
    .summary .item { font-size: 12px; }
    .summary .add { color: #166534; }
    .summary .del { color: #991b1b; }
    .summary .hint { margin-left: auto; color: #64748b; }
    .view { display: none; flex: 1; min-height: 0; }
    .view.active { display: flex; flex-direction: column; }
    .column-headings, .diff-grid, .pdf-grid { display: grid; grid-template-columns: 1fr 1fr; }
    .column-heading { padding: 8px 12px; border-bottom: 1px solid #e2e8f0; background: #f8fafc; color: #334155; font-size: 12px; font-weight: 600; }
    .column-heading + .column-heading, .diff-cell + .diff-cell, .pdf-panel + .pdf-panel { border-left: 1px solid #e2e8f0; }
    .diff-scroll { flex: 1; overflow: auto; min-height: 0; }
    .diff-grid { align-items: stretch; font: 12px/1.6 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace; }
    .diff-cell { min-width: 0; padding: 4px 12px; border-bottom: 1px solid #f1f5f9; white-space: pre-wrap; overflow-wrap: anywhere; }
    .diff-cell.add { border-left: 4px solid #22c55e; background: #dcfce7; color: #166534; }
    .diff-cell.del { border-left: 4px solid #ef4444; background: #fee2e2; color: #991b1b; text-decoration: line-through; }
    .diff-cell.gap { background: #f8fafc; }
    .empty { grid-column: 1 / -1; padding: 24px; color: #64748b; text-align: center; }
    .pdf-grid { flex: 1; min-height: 0; }
    .pdf-panel { position: relative; min-height: 0; }
    iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
  `;

  let addCount = 0;
  let delCount = 0;
  diff.forEach((part) => {
    if (part.added) addCount += countWords(part.value);
    if (part.removed) delCount += countWords(part.value);
  });

  const body =
    diff
      .map((part) => {
        const escaped = escapeHtml(part.value);
        if (part.added) {
          return `<div class="diff-cell gap"></div><div class="diff-cell add">+ ${escaped}</div>`;
        }
        if (part.removed) {
          return `<div class="diff-cell del">- ${escaped}</div><div class="diff-cell gap"></div>`;
        }
        return `<div class="diff-cell">${escaped}</div><div class="diff-cell">${escaped}</div>`;
      })
      .join("") ||
    '<div class="empty">No readable PDF text was found to compare. Use the Original PDFs tab to inspect the files side by side.</div>';

  const pdfTab = hasOriginalPdfs
    ? '<button type="button" data-view="pdfView">Original PDFs</button>'
    : "";
  const originalPdfs = hasOriginalPdfs
    ? `
    <section id="pdfView" class="view">
      <div class="column-headings">
        <div class="column-heading">Stored: ${escapeHtml(storedLabel)}</div>
        <div class="column-heading">Current: ${escapeHtml(currentLabel)}</div>
      </div>
      <div class="pdf-grid">
        <div class="pdf-panel"><iframe title="Stored PDF" src="${escapeHtml(previousPdfUrl)}"></iframe></div>
        <div class="pdf-panel"><iframe title="Current PDF" src="${escapeHtml(currentPdfUrl)}"></iframe></div>
      </div>
    </section>`
    : "";

  return `
<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    ${faviconUrl ? `<link rel="icon" href="${escapeHtml(faviconUrl)}" />` : ""}
    <style>${styles}</style>
  </head>
  <body>
    <header>
      <div>
        <h1>${escapeHtml(title)}</h1>
        <div class="meta">Comparing stored PDF "${escapeHtml(storedLabel)}" with the current PDF</div>
      </div>
      <div class="toolbar">
        <button type="button" class="active" data-view="diffView">Highlighted differences</button>
        ${pdfTab}
      </div>
    </header>
    <div class="summary">
      <div class="item del">Removed words: ${delCount}</div>
      <div class="item add">Added words: ${addCount}</div>
      <div class="item hint">Red = removed from stored PDF. Green = added in current PDF.</div>
    </div>
    <section id="diffView" class="view active">
      <div class="column-headings">
        <div class="column-heading">Stored: ${escapeHtml(storedLabel)}</div>
        <div class="column-heading">Current: ${escapeHtml(currentLabel)}</div>
      </div>
      <div class="diff-scroll">
        <div class="diff-grid">${body}</div>
      </div>
    </section>
    ${originalPdfs}
    <script>
      document.querySelectorAll("[data-view]").forEach((button) => {
        button.addEventListener("click", () => {
          document.querySelectorAll("[data-view]").forEach((item) => item.classList.remove("active"));
          document.querySelectorAll(".view").forEach((view) => view.classList.remove("active"));
          button.classList.add("active");
          document.getElementById(button.dataset.view).classList.add("active");
        });
      });
    </script>
  </body>
</html>`;
}

function createCanvas(width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

async function renderPdfPages(buffer) {
  const loadingTask = getDocument({ data: new Uint8Array(buffer) });
  const pdf = await loadingTask.promise;
  const pages = [];

  try {
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const viewport = page.getViewport({ scale: PDF_RENDER_SCALE });
      const canvas = createCanvas(
        Math.ceil(viewport.width),
        Math.ceil(viewport.height)
      );
      const context = canvas.getContext("2d", { alpha: false });

      await page.render({ canvasContext: context, viewport }).promise;
      pages.push(canvas);
    }
  } finally {
  }

  return pages;
}

function cloneCanvas(source, width, height) {
  const canvas = createCanvas(width, height);
  const context = canvas.getContext("2d", { alpha: false });
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  if (source) context.drawImage(source, 0, 0);
  return canvas;
}

function findHighlightBoxes(tileCounts, tileColumns, tileRows, width, height) {
  const visited = new Uint8Array(tileCounts.length);
  const boxes = [];

  for (let tileIndex = 0; tileIndex < tileCounts.length; tileIndex += 1) {
    if (
      visited[tileIndex] ||
      tileCounts[tileIndex] < MIN_CHANGED_PIXELS_PER_TILE
    ) {
      continue;
    }

    const queue = [tileIndex];
    let queueIndex = 0;
    let minColumn = tileIndex % tileColumns;
    let maxColumn = minColumn;
    let minRow = Math.floor(tileIndex / tileColumns);
    let maxRow = minRow;
    let changedPixels = 0;
    visited[tileIndex] = 1;

    while (queueIndex < queue.length) {
      const currentIndex = queue[queueIndex++];
      const column = currentIndex % tileColumns;
      const row = Math.floor(currentIndex / tileColumns);
      changedPixels += tileCounts[currentIndex];
      minColumn = Math.min(minColumn, column);
      maxColumn = Math.max(maxColumn, column);
      minRow = Math.min(minRow, row);
      maxRow = Math.max(maxRow, row);

      for (let rowOffset = -1; rowOffset <= 1; rowOffset += 1) {
        for (let columnOffset = -1; columnOffset <= 1; columnOffset += 1) {
          const nextColumn = column + columnOffset;
          const nextRow = row + rowOffset;
          if (
            nextColumn < 0 ||
            nextColumn >= tileColumns ||
            nextRow < 0 ||
            nextRow >= tileRows
          ) {
            continue;
          }

          const nextIndex = nextRow * tileColumns + nextColumn;
          if (
            !visited[nextIndex] &&
            tileCounts[nextIndex] >= MIN_CHANGED_PIXELS_PER_TILE
          ) {
            visited[nextIndex] = 1;
            queue.push(nextIndex);
          }
        }
      }
    }

    if (changedPixels >= MIN_CHANGED_PIXELS_PER_BOX) {
      const left = Math.max(0, minColumn * DIFF_TILE_SIZE - HIGHLIGHT_PADDING);
      const top = Math.max(0, minRow * DIFF_TILE_SIZE - HIGHLIGHT_PADDING);
      const right = Math.min(
        width,
        (maxColumn + 1) * DIFF_TILE_SIZE + HIGHLIGHT_PADDING
      );
      const bottom = Math.min(
        height,
        (maxRow + 1) * DIFF_TILE_SIZE + HIGHLIGHT_PADDING
      );
      boxes.push({ left, top, width: right - left, height: bottom - top });
    }
  }

  return boxes;
}

function drawHighlightBoxes(context, boxes, color) {
  context.save();
  context.lineWidth = 3;
  context.strokeStyle = color;
  boxes.forEach(({ left, top, width, height }) => {
    context.strokeRect(left, top, width, height);
  });
  context.restore();
}

function highlightPageDifferences(previousPage, currentPage) {
  const width = Math.max(previousPage?.width || 0, currentPage?.width || 0);
  const height = Math.max(previousPage?.height || 0, currentPage?.height || 0);
  const previousCanvas = cloneCanvas(previousPage, width, height);
  const currentCanvas = cloneCanvas(currentPage, width, height);
  const previousContext = previousCanvas.getContext("2d", { alpha: false });
  const currentContext = currentCanvas.getContext("2d", { alpha: false });
  const previousImage = previousContext.getImageData(0, 0, width, height);
  const currentImage = currentContext.getImageData(0, 0, width, height);
  const tileColumns = Math.ceil(width / DIFF_TILE_SIZE);
  const tileRows = Math.ceil(height / DIFF_TILE_SIZE);
  const tileCounts = new Uint16Array(tileColumns * tileRows);
  let differenceCount = 0;

  for (let index = 0; index < previousImage.data.length; index += 4) {
    const difference =
      Math.abs(previousImage.data[index] - currentImage.data[index]) +
      Math.abs(previousImage.data[index + 1] - currentImage.data[index + 1]) +
      Math.abs(previousImage.data[index + 2] - currentImage.data[index + 2]);

    if (difference >= PIXEL_DIFF_THRESHOLD) {
      differenceCount += 1;
      const pixelIndex = index / 4;
      const x = pixelIndex % width;
      const y = Math.floor(pixelIndex / width);
      const tileIndex =
        Math.floor(y / DIFF_TILE_SIZE) * tileColumns +
        Math.floor(x / DIFF_TILE_SIZE);
      tileCounts[tileIndex] += 1;
    }
  }

  const boxes = findHighlightBoxes(
    tileCounts,
    tileColumns,
    tileRows,
    width,
    height
  );
  drawHighlightBoxes(previousContext, boxes, "#dc2626");
  drawHighlightBoxes(currentContext, boxes, "#16a34a");

  return { previousCanvas, currentCanvas, differenceCount };
}

function canvasToObjectUrl(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Could not create PDF comparison image."));
        return;
      }
      resolve(URL.createObjectURL(blob));
    }, "image/png");
  });
}

async function createVisualDiffPages(previousBuffer, currentBuffer) {
  const [previousPages, currentPages] = await Promise.all([
    renderPdfPages(previousBuffer),
    renderPdfPages(currentBuffer),
  ]);
  const pageCount = Math.max(previousPages.length, currentPages.length);
  const pages = [];

  for (let index = 0; index < pageCount; index += 1) {
    const { previousCanvas, currentCanvas, differenceCount } =
      highlightPageDifferences(previousPages[index], currentPages[index]);
    const [previousImageUrl, currentImageUrl] = await Promise.all([
      canvasToObjectUrl(previousCanvas),
      canvasToObjectUrl(currentCanvas),
    ]);
    pages.push({
      pageNumber: index + 1,
      previousImageUrl,
      currentImageUrl,
      differenceCount,
    });
  }

  return pages;
}

function generateVisualDiffHtml({
  title,
  storedLabel,
  previousPdfUrl,
  currentPdfUrl,
  faviconUrl,
  pages,
}) {
  const changedPixels = pages.reduce(
    (total, page) => total + page.differenceCount,
    0
  );
  const changedPages = pages.filter((page) => page.differenceCount > 0).length;
  const pageRows = pages
    .map(
      (page) => `
        <section class="page-row">
          <div class="page-panel">
            <div class="page-label">${escapeHtml(storedLabel)} page ${page.pageNumber}</div>
            <img src="${escapeHtml(page.previousImageUrl)}" alt="${escapeHtml(storedLabel)} page ${page.pageNumber}" />
          </div>
          <div class="page-panel">
            <div class="page-label">Current page ${page.pageNumber}</div>
            <img src="${escapeHtml(page.currentImageUrl)}" alt="Current PDF page ${page.pageNumber}" />
          </div>
        </section>`
    )
    .join("");

  return `
<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <link rel="icon" href="${escapeHtml(faviconUrl)}" />
    <style>
      * { box-sizing: border-box; }
      html, body { height: 100%; margin: 0; }
      body { display: flex; flex-direction: column; color: #334155; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; }
      header { display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: #0f172a; color: #fff; }
      header h1 { margin: 0; font-size: 16px; font-weight: 600; }
      header .meta { margin-top: 4px; opacity: 0.85; font-size: 12px; }
      .toolbar { display: flex; gap: 8px; margin-left: auto; }
      button { padding: 6px 10px; border: 1px solid #475569; border-radius: 6px; background: #1e293b; color: #fff; cursor: pointer; font-size: 12px; }
      button:hover, button.active { background: #475569; }
      .summary { display: flex; gap: 18px; padding: 10px 16px; border-bottom: 1px solid #e2e8f0; background: #f8fafc; font-size: 12px; }
      .summary .del { color: #991b1b; }
      .summary .add { color: #166534; }
      .summary .hint { margin-left: auto; color: #64748b; }
      .view { display: none; flex: 1; min-height: 0; }
      .view.active { display: flex; flex-direction: column; }
      .pages { flex: 1; overflow: auto; padding: 16px; background: #e2e8f0; }
      .page-row { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 16px; margin: 0 auto 16px; max-width: 1800px; align-items: start; }
      .page-panel { min-width: 0; overflow: hidden; border: 1px solid #cbd5e1; border-radius: 4px; background: #fff; box-shadow: 0 1px 3px rgb(15 23 42 / 15%); }
      .page-label, .column-heading { padding: 8px 12px; border-bottom: 1px solid #e2e8f0; background: #f8fafc; font-size: 12px; font-weight: 600; }
      img { display: block; width: 100%; height: auto; }
      .column-headings, .pdf-grid { display: grid; grid-template-columns: 1fr 1fr; }
      .column-heading + .column-heading, .pdf-panel + .pdf-panel { border-left: 1px solid #e2e8f0; }
      .pdf-grid { flex: 1; min-height: 0; }
      .pdf-panel { position: relative; min-height: 0; }
      iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
    </style>
  </head>
  <body>
    <header>
      <div>
        <h1>${escapeHtml(title)}</h1>
        <div class="meta">Comparing stored PDF "${escapeHtml(storedLabel)}" with the current PDF</div>
      </div>
      <div class="toolbar">
        <button type="button" class="active" data-view="diffView">Highlighted pages</button>
        <button type="button" data-view="pdfView">Original PDFs</button>
      </div>
    </header>
    <div class="summary">
      <div>Changed pages: ${changedPages} / ${pages.length}</div>
      <div>Changed pixels: ${changedPixels.toLocaleString()}</div>
      <div class="hint"><span class="del">Red outline = stored PDF difference.</span> <span class="add">Green outline = current PDF difference.</span></div>
    </div>
    <section id="diffView" class="view active">
      <div class="pages">${pageRows}</div>
    </section>
    <section id="pdfView" class="view">
      <div class="column-headings">
        <div class="column-heading">Stored: ${escapeHtml(storedLabel)}</div>
        <div class="column-heading">Current: ${escapeHtml(title)}</div>
      </div>
      <div class="pdf-grid">
        <div class="pdf-panel"><iframe title="Stored PDF" src="${escapeHtml(previousPdfUrl)}"></iframe></div>
        <div class="pdf-panel"><iframe title="Current PDF" src="${escapeHtml(currentPdfUrl)}"></iframe></div>
      </div>
    </section>
    <script>
      document.querySelectorAll("[data-view]").forEach((button) => {
        button.addEventListener("click", () => {
          document.querySelectorAll("[data-view]").forEach((item) => item.classList.remove("active"));
          document.querySelectorAll(".view").forEach((view) => view.classList.remove("active"));
          button.classList.add("active");
          document.getElementById(button.dataset.view).classList.add("active");
        });
      });
    </script>
  </body>
</html>`;
}

function generateSideBySideHtml({
  title,
  storedLabel,
  previousPdfUrl,
  currentPdfUrl,
  faviconUrl,
}) {
  return `
<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)}</title>
    <link rel="icon" href="${escapeHtml(faviconUrl)}" />
    <style>
      * { box-sizing: border-box; }
      html, body { height: 100%; margin: 0; }
      body { display: flex; flex-direction: column; color: #334155; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; }
      header { padding: 12px 16px; background: #0f172a; color: #fff; }
      header h1 { margin: 0; font-size: 16px; font-weight: 600; }
      header .meta { margin-top: 4px; opacity: 0.85; font-size: 12px; }
      .column-headings, .pdf-grid { display: grid; grid-template-columns: 1fr 1fr; }
      .column-heading { padding: 8px 12px; border-bottom: 1px solid #e2e8f0; background: #f8fafc; font-size: 12px; font-weight: 600; }
      .column-heading + .column-heading, .pdf-panel + .pdf-panel { border-left: 1px solid #e2e8f0; }
      .pdf-grid { flex: 1; min-height: 0; }
      .pdf-panel { position: relative; min-height: 0; }
      iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
    </style>
  </head>
  <body>
    <header>
      <h1>${escapeHtml(title)}</h1>
      <div class="meta">Viewing stored PDF "${escapeHtml(storedLabel)}" beside the current PDF without difference highlights</div>
    </header>
    <div class="column-headings">
      <div class="column-heading">Stored: ${escapeHtml(storedLabel)}</div>
      <div class="column-heading">Current: ${escapeHtml(title)}</div>
    </div>
    <div class="pdf-grid">
      <div class="pdf-panel"><iframe title="Stored PDF" src="${escapeHtml(previousPdfUrl)}"></iframe></div>
      <div class="pdf-panel"><iframe title="Current PDF" src="${escapeHtml(currentPdfUrl)}"></iframe></div>
    </div>
  </body>
</html>`;
}

export function clearStoredPdf() {
  localStorage.removeItem(STORAGE_KEY);
}

export function hasStoredPdf() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.length > 0
      : typeof parsed === "string";
  } catch (_) {
    return false;
  }
}

function normalizePdfType(pdfType, label = "") {
  if (pdfType === "draft" || pdfType === "final") return pdfType;
  return /\bdraft\b/i.test(label) ? "draft" : "final";
}

function limitStoredPdfsByType(store) {
  const typeCounts = { draft: 0, final: 0 };

  return store.filter((entry) => {
    const pdfType = normalizePdfType(entry.pdfType, entry.label);
    if (typeCounts[pdfType] >= MAX_STORED_PDFS_PER_TYPE) return false;
    typeCounts[pdfType] += 1;
    return true;
  });
}

// Store up to three recent PDFs for each comparison type.
export async function storeCurrentPdf(doc, label = "PDF", pdfType = "final") {
  const arrayBuffer = await doc.output("arraybuffer");
  const base64 = arrayBufferToBase64(arrayBuffer);
  let store = [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) store = parsed;
    }
  } catch (_) {}
  const ts = Date.now();
  const entry = {
    id: `${ts}`,
    label,
    pdfType: normalizePdfType(pdfType, label),
    ts,
    base64,
  };
  try {
    const next = limitStoredPdfsByType([entry, ...store]);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (_) {}
  return entry;
}

export function listStoredPdfs(pdfType) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(list)) return [];
    const normalizedPdfType = pdfType ? normalizePdfType(pdfType) : undefined;
    return list
      .map(({ id, label, pdfType: storedPdfType, ts }) => ({
        id,
        label,
        pdfType: normalizePdfType(storedPdfType, label),
        ts,
      }))
      .filter(
        (entry) => !normalizedPdfType || entry.pdfType === normalizedPdfType
      )
      .slice(0, MAX_STORED_PDFS_PER_TYPE);
  } catch (_) {
    return [];
  }
}

export async function compareWithStoredById(doc, targetId, options = {}) {
  const {
    title = "PDF Diff",
    mode = "visual",
    highlightDifferences = true,
    pdfType,
  } = options;
  const arrayBuffer = await doc.output("arraybuffer");
  const base64 = arrayBufferToBase64(arrayBuffer);
  let store = [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) store = JSON.parse(raw) || [];
  } catch (_) {}
  const target = Array.isArray(store)
    ? store.find(
        (entry) =>
          entry.id === targetId &&
          (!pdfType ||
            normalizePdfType(entry.pdfType, entry.label) ===
              normalizePdfType(pdfType))
      )
    : undefined;
  if (target) {
    if (mode === "text") {
      const prevBuf = base64ToArrayBuffer(target.base64);
      const newBuf = base64ToArrayBuffer(base64);
      const prevText = extractTextFromArrayBuffer(prevBuf);
      const newText = extractTextFromArrayBuffer(newBuf);
      const html = generateDiffHtml(prevText, newText, title, {
        storedLabel: target.label,
        currentLabel: title,
      });
      const htmlBlob = new Blob([html], { type: "text/html" });
      const htmlUrl = URL.createObjectURL(htmlBlob);
      window.open(htmlUrl, "_blank");
    } else {
      const prevBuf = base64ToArrayBuffer(target.base64);
      const prevPdfUrl = URL.createObjectURL(
        new Blob([prevBuf], { type: "application/pdf" })
      );
      const currentPdfUrl = URL.createObjectURL(
        new Blob([arrayBuffer], { type: "application/pdf" })
      );

      const faviconUrl = `${window.location.origin}/favicon.ico`;
      const sharedOptions = {
        title,
        storedLabel: target.label,
        previousPdfUrl: prevPdfUrl,
        currentPdfUrl,
        faviconUrl,
      };
      const html = highlightDifferences
        ? generateVisualDiffHtml({
            ...sharedOptions,
            pages: await createVisualDiffPages(prevBuf, arrayBuffer),
          })
        : generateSideBySideHtml(sharedOptions);
      const htmlBlob = new Blob([html], { type: "text/html" });
      const htmlUrl = URL.createObjectURL(htmlBlob);
      window.open(htmlUrl, "_blank");
    }
  }
}

export async function compareWithPdfBase64(doc, previousBase64, options = {}) {
  const {
    title = "PDF Diff",
    mode = "visual",
    highlightDifferences = true,
    storedLabel = "Stored PDF",
  } = options;

  const arrayBuffer = await doc.output("arraybuffer");
  const cleanPreviousBase64 = String(previousBase64 || "")
    .replace(/^data:application\/pdf(?:;[^,]*)?;base64,/i, "")
    .trim();
  const prevBuf = base64ToArrayBuffer(cleanPreviousBase64);

  if (mode === "text") {
    const prevText = extractTextFromArrayBuffer(prevBuf);
    const newText = extractTextFromArrayBuffer(arrayBuffer);
    const html = generateDiffHtml(prevText, newText, title, {
      storedLabel,
      currentLabel: title,
    });
    const htmlBlob = new Blob([html], { type: "text/html" });
    const htmlUrl = URL.createObjectURL(htmlBlob);
    window.open(htmlUrl, "_blank");
    return;
  }

  const prevPdfUrl = URL.createObjectURL(
    new Blob([prevBuf], { type: "application/pdf" })
  );
  const currentPdfUrl = URL.createObjectURL(
    new Blob([arrayBuffer], { type: "application/pdf" })
  );
  const faviconUrl = `${window.location.origin}/favicon.ico`;
  const sharedOptions = {
    title,
    storedLabel,
    previousPdfUrl: prevPdfUrl,
    currentPdfUrl,
    faviconUrl,
  };
  const html = highlightDifferences
    ? generateVisualDiffHtml({
        ...sharedOptions,
        pages: await createVisualDiffPages(prevBuf, arrayBuffer),
      })
    : generateSideBySideHtml(sharedOptions);
  const htmlBlob = new Blob([html], { type: "text/html" });
  const htmlUrl = URL.createObjectURL(htmlBlob);
  window.open(htmlUrl, "_blank");
}

export async function comparePdfBase64WithBlob(
  previousBase64,
  currentBlob,
  options = {}
) {
  const {
    title = "PDF Diff",
    mode = "visual",
    highlightDifferences = true,
    storedLabel = "Stored PDF",
    openMode = "newTab",
    targetWindow,
  } = options;

  const openComparisonHtml = (html) => {
    const htmlBlob = new Blob([html], { type: "text/html" });
    const htmlUrl = URL.createObjectURL(htmlBlob);

    if (openMode === "returnUrl") {
      return htmlUrl;
    }

    if (
      openMode === "targetWindow" &&
      targetWindow &&
      !targetWindow.closed
    ) {
      targetWindow.location.href = htmlUrl;
      return;
    }

    window.open(htmlUrl, "_blank");
    return htmlUrl;
  };

  const cleanPreviousBase64 = String(previousBase64 || "")
    .replace(/^data:application\/pdf(?:;[^,]*)?;base64,/i, "")
    .trim();
  const prevBuf = base64ToArrayBuffer(cleanPreviousBase64);
  const currentBuffer = await currentBlob.arrayBuffer();

  if (mode === "text") {
    const prevText = extractTextFromArrayBuffer(prevBuf);
    const newText = extractTextFromArrayBuffer(currentBuffer);
    const html = generateDiffHtml(prevText, newText, title, {
      storedLabel,
      currentLabel: title,
    });
    return openComparisonHtml(html);
  }

  const prevPdfUrl = URL.createObjectURL(
    new Blob([prevBuf], { type: "application/pdf" })
  );
  const currentPdfUrl = URL.createObjectURL(
    new Blob([currentBuffer], { type: "application/pdf" })
  );
  const faviconUrl = `${window.location.origin}/favicon.ico`;
  const sharedOptions = {
    title,
    storedLabel,
    previousPdfUrl: prevPdfUrl,
    currentPdfUrl,
    faviconUrl,
  };
  const html = highlightDifferences
    ? generateVisualDiffHtml({
        ...sharedOptions,
        pages: await createVisualDiffPages(prevBuf, currentBuffer),
      })
    : generateSideBySideHtml(sharedOptions);
  return openComparisonHtml(html);
}
